/**
 * @voltxampere/web — Simulation defaults preferences (Sprint 24 A3)
 *
 * AYARLAR > Simülasyon sub-tab'ının placeholder gösterimini gerçek
 * state'e bağlar. Sprint 21 D-21-2 + Sprint 23 C1'de persistence
 * altyapısı (ACDefaults / TransientDefaults / temperature) shipped,
 * UI bağlama Sprint 24 A3 closure'a kaldı.
 *
 * Default'lar BLUEPRINT §6 (AC) + §3.9 (Transient) + ngspice 27°C:
 *   AC:        fStart=1Hz, fStop=1MHz, points=21 (per dec), sweep="dec"
 *   Transient: tStart=0, tStop=10ms, maxStep=10µs
 *   Temp:      300.15 K (= 27°C)
 *
 * Public API:
 *  - acDefaults / transientDefaults / temperatureK signal'ları
 *  - setACDefaults / setTransientDefaults / setTemperatureCelsius mutators
 *  - resetSimPrefs() — tüm üçünü factory default'a geri sar
 *  - initSimPrefs() — boot path'inde IDB hydrate
 *
 * Kelvin ↔ Celsius dönüşümü: T_K = T_C + 273.15. UI Celsius
 * gösterir, IDB Kelvin saklar (engine SI birim contract).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import {
  getSettings,
  mergeSettings,
  type ACDefaults,
  type TransientDefaults,
} from "@voltxampere/persistence";

export const DEFAULT_AC: ACDefaults = {
  fStart: 1,
  fStop: 1_000_000,
  points: 21,
  sweep: "dec",
} as const;

export const DEFAULT_TRANSIENT: TransientDefaults = {
  tStart: 0,
  tStop: 0.01,
  maxStep: 1e-5,
} as const;

/** ngspice canonical default temperature: 27°C = 300.15 K. */
export const DEFAULT_TEMPERATURE_K = 300.15 as const;
export const TEMPERATURE_MIN_C = 0 as const;
export const TEMPERATURE_MAX_C = 200 as const;

export const acDefaults = signal<ACDefaults>(DEFAULT_AC);
export const transientDefaults = signal<TransientDefaults>(DEFAULT_TRANSIENT);
export const temperatureK = signal<number>(DEFAULT_TEMPERATURE_K);

let initialised = false;

export async function initSimPrefs(): Promise<void> {
  if (initialised) return;
  initialised = true;
  try {
    const settings = await getSettings();
    if (settings?.acDefaults) {
      acDefaults.value = settings.acDefaults;
    }
    if (settings?.transientDefaults) {
      transientDefaults.value = settings.transientDefaults;
    }
    if (typeof settings?.temperature === "number" && Number.isFinite(settings.temperature)) {
      temperatureK.value = settings.temperature;
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA sim-prefs] hydrate failed:", err);
    }
  }
}

function persistAC(): void {
  void mergeSettings({ acDefaults: acDefaults.value }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA sim-prefs] AC persist failed:", err);
    }
  });
}

function persistTransient(): void {
  void mergeSettings({ transientDefaults: transientDefaults.value }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA sim-prefs] transient persist failed:", err);
    }
  });
}

function persistTemperature(): void {
  void mergeSettings({ temperature: temperatureK.value }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA sim-prefs] temperature persist failed:", err);
    }
  });
}

export function setACDefaults(next: Partial<ACDefaults>): void {
  acDefaults.value = { ...acDefaults.value, ...next };
  persistAC();
}

export function setTransientDefaults(next: Partial<TransientDefaults>): void {
  transientDefaults.value = { ...transientDefaults.value, ...next };
  persistTransient();
}

/** UI passes Celsius; we store Kelvin per engine SI contract. */
export function setTemperatureCelsius(celsius: number): void {
  if (!Number.isFinite(celsius)) return;
  const clamped = Math.min(Math.max(celsius, TEMPERATURE_MIN_C), TEMPERATURE_MAX_C);
  temperatureK.value = clamped + 273.15;
  persistTemperature();
}

export function temperatureCelsius(): number {
  return temperatureK.value - 273.15;
}

/** Reset all three groups to factory defaults. */
export function resetSimPrefs(): void {
  acDefaults.value = DEFAULT_AC;
  transientDefaults.value = DEFAULT_TRANSIENT;
  temperatureK.value = DEFAULT_TEMPERATURE_K;
  void mergeSettings({
    acDefaults: DEFAULT_AC,
    transientDefaults: DEFAULT_TRANSIENT,
    temperature: DEFAULT_TEMPERATURE_K,
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA sim-prefs] reset persist failed:", err);
    }
  });
}

export function __resetSimPrefsForTests(): void {
  acDefaults.value = DEFAULT_AC;
  transientDefaults.value = DEFAULT_TRANSIENT;
  temperatureK.value = DEFAULT_TEMPERATURE_K;
  initialised = false;
}
